import { Checkbox, CircularProgress, FormControlLabel } from '@material-ui/core'
import React from 'react'

import { createSummerCampBatches } from 'api'
import Button from 'app/components/generic/Button'
import constants from 'app/constants'
import { eventAlert, showToast } from 'app/helpers'

import {
  EMPTY_MANUAL_ROW,
  EXPECTED_CSV_COLUMNS,
  isManualRowFilled,
  manualRowToCsvRow
} from './config'
import CsvUploadPanel from './CsvUploadPanel'
import {
  fetchAllCentres,
  fetchAllCourses,
  fetchAllRooms,
  fetchAllTeachers
} from './lookups'
import ManualRowsPanel from './ManualRowsPanel'
import ResultsPanel from './ResultsPanel'
import {
  ActionsRow,
  Card,
  ColumnChip,
  ColumnsRefRow,
  Header,
  LeftActions,
  Page,
  RightActions,
  SectionLabel,
  SubTitle,
  Tab,
  Tabs,
  Title
} from './styles'

const MODES = { CSV: 'csv', MANUAL: 'manual' }

const SummerCampBatch = () => {
  const [mode, setMode] = React.useState(MODES.CSV)
  const [file, setFile] = React.useState(null)
  const [rows, setRows] = React.useState([{ ...EMPTY_MANUAL_ROW }])
  const [dryRun, setDryRun] = React.useState(true)
  const [loading, setLoading] = React.useState(false)
  const [result, setResult] = React.useState(null)

  const [centres, setCentres] = React.useState([])
  const [courses, setCourses] = React.useState([])
  const [teachers, setTeachers] = React.useState([])
  const [rooms, setRooms] = React.useState([])
  const [lookupsLoading, setLookupsLoading] = React.useState(false)

  React.useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLookupsLoading(true)
      try {
        const [c, co, t, r] = await Promise.all([
          fetchAllCentres(),
          fetchAllCourses(),
          fetchAllTeachers(),
          fetchAllRooms()
        ])
        if (cancelled) return
        setCentres(c)
        setCourses(co)
        setTeachers(t)
        setRooms(r)
      } catch (error) {
        eventAlert(
          error?.message || 'Failed to load dropdown options',
          constants.ERROR
        )
      } finally {
        if (!cancelled) setLookupsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const canSubmit = React.useMemo(() => {
    if (loading) return false
    if (mode === MODES.CSV) return !!file
    return rows.some(isManualRowFilled)
  }, [loading, mode, file, rows])

  const handleRun = async () => {
    setLoading(true)
    setResult(null)
    try {
      const payload =
        mode === MODES.CSV
          ? { file, dryRun }
          : {
              rows: rows.filter(isManualRowFilled).map(manualRowToCsvRow),
              dryRun
            }
      const response = await createSummerCampBatches(payload)
      const data = response?.data || response
      setResult(data)
      const created = data?.counts?.created || 0
      const errors = data?.counts?.error || 0
      const dr = data?.counts?.dry_run || 0
      if (dryRun) {
        showToast(
          `dry run complete: ${dr} ok, ${errors} error(s)`,
          errors > 0 ? constants.ERROR : constants.SUCCESS
        )
      } else if (errors > 0 && created === 0) {
        showToast(`all ${errors} row(s) failed`, constants.ERROR)
      } else if (errors > 0) {
        showToast(
          `partial: ${created} created, ${errors} error(s)`,
          constants.INFO
        )
      } else {
        showToast(`${created} batch(es) created`, constants.SUCCESS)
      }
    } catch (error) {
      eventAlert(
        error?.message || 'Failed to process summer camp batches',
        constants.ERROR
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page>
      <Header>
        <Title variant="h5">summer camp batches</Title>
        <SubTitle>
          bulk-create summer camp batches from a CSV (same format as the
          `create_summer_camp_batches` management command) or by entering rows
          manually. dry-run validates without creating.
        </SubTitle>
      </Header>

      <Card>
        <SectionLabel>expected csv columns</SectionLabel>
        <ColumnsRefRow>
          {EXPECTED_CSV_COLUMNS.map((c) => (
            <ColumnChip key={c}>{c}</ColumnChip>
          ))}
        </ColumnsRefRow>
        <SubTitle>
          headers are matched case-insensitively. dates accept d/m/Y or Y-m-d;
          times accept 9:30 or 9-30; rooms accept Room 12 or 12.
        </SubTitle>
      </Card>

      <Tabs>
        <Tab
          type="button"
          $active={mode === MODES.CSV}
          onClick={() => setMode(MODES.CSV)}
        >
          csv upload
        </Tab>
        <Tab
          type="button"
          $active={mode === MODES.MANUAL}
          onClick={() => setMode(MODES.MANUAL)}
        >
          manual entry
        </Tab>
      </Tabs>

      {mode === MODES.CSV ? (
        <CsvUploadPanel file={file} onFileChange={setFile} />
      ) : (
        <ManualRowsPanel
          rows={rows}
          onChange={setRows}
          centres={centres}
          courses={courses}
          teachers={teachers}
          rooms={rooms}
          loading={lookupsLoading}
        />
      )}

      <ActionsRow>
        <LeftActions>
          <FormControlLabel
            control={
              <Checkbox
                checked={dryRun}
                onChange={(e) => setDryRun(e.target.checked)}
                color="primary"
              />
            }
            label="dry run (validate only, don't create)"
          />
        </LeftActions>
        <RightActions>
          {loading ? <CircularProgress size={22} /> : null}
          <Button disabled={!canSubmit} onClick={handleRun}>
            {dryRun ? 'validate' : 'create batches'}
          </Button>
        </RightActions>
      </ActionsRow>

      <ResultsPanel result={result} />
    </Page>
  )
}

export default SummerCampBatch
